import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Package, 
  MessageSquare, 
  LogOut, 
  Menu, 
  X,
  Sparkles,
  Moon
} from "lucide-react";
import { Button } from "@/components/ui/button";

const AdminLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if admin is authenticated
    const auth = sessionStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  const navItems = [
    { path: "/admin/dashboard", icon: <LayoutDashboard className="w-5 h-5" />, label: "Dashboard" },
    { path: "/admin/products", icon: <Package className="w-5 h-5" />, label: "Products" },
    { path: "/admin/inquiries", icon: <MessageSquare className="w-5 h-5" />, label: "Inquiries" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-mystic-void flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 admin-sidebar border-r border-white/10">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_crystal-haven-2/artifacts/y9uaa7nd_FullLogo_Transparent.png" 
              alt="Krystalz" 
              className="h-28 w-auto brightness-125 contrast-110"
            />
          </Link>
          <p className="text-xs text-mystic-mist/60 mt-2 flex items-center gap-1">
            <Moon className="w-3 h-3" /> Admin Portal
          </p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.path)
                  ? "bg-mystic-amethyst/20 text-mystic-gold border border-mystic-amethyst/30"
                  : "text-mystic-mist/70 hover:bg-white/5 hover:text-mystic-silver"
              }`}
              data-testid={`sidebar-${item.label.toLowerCase()}`}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-mystic-mist/70 hover:text-mystic-orchid hover:bg-white/5"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm" 
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-64 admin-sidebar border-r border-white/10">
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_crystal-haven-2/artifacts/y9uaa7nd_FullLogo_Transparent.png" 
                  alt="Krystalz" 
                  className="h-28 w-auto brightness-125 contrast-110"
                />
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="text-mystic-silver">
                <X className="w-6 h-6" />
              </button>
            </div>

            <nav className="flex-1 p-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? "bg-mystic-amethyst/20 text-mystic-gold"
                      : "text-mystic-mist/70 hover:bg-white/5 hover:text-mystic-silver"
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="p-4 border-t border-white/10">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start text-mystic-mist/70 hover:text-mystic-orchid hover:bg-white/5"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-mystic-deep/80 backdrop-blur-md border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg text-mystic-silver"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="font-serif text-2xl text-mystic-silver italic">{title}</h1>
          </div>
          <Link 
            to="/" 
            className="text-sm text-mystic-mist hover:text-mystic-amethyst transition-colors"
            data-testid="view-site-link"
          >
            View Site →
          </Link>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
